export function formatTimestamp(timestamp: number | bigint | undefined): string {
  if (!timestamp) return '-';
  const date = new Date(Number(timestamp) / 1000000);
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

/** ISO 时间字符串格式化。 */
export function fmtTime(iso?: string): string {
  if (!iso) return '-';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/** Token 有效期：0 / undefined 视为永久。 */
export function formatExpire(timestamp: number | bigint | undefined): string {
  if (!timestamp || timestamp === 0n) return '永久';
  return formatTimestamp(timestamp);
}

/** 将本地日期字符串（YYYY-MM-DD）转为当天 23:59:59 的 unix nano。 */
export function localDateToExpireNano(localDate: string): bigint {
  if (!localDate) return 0n;
  const ms = new Date(`${localDate}T23:59:59`).getTime();
  return BigInt(ms) * 1_000_000n;
}

/** 复制到剪贴板（异步，调用方自行处理 message 反馈）。返回是否成功。 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** 格式化 expireAt(unix nano, bigint) 为 YYYY-MM-DD HH:mm；0 表示永久。 */
export function fmtExpire(nano: bigint): string {
  if (nano === 0n) return '永久';
  const ms = Number(nano / 1_000_000n);
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return '-';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${day} ${hh}:${mm}`;
}

/** 把毫秒时间戳格式化为「N 分钟前 / N 小时前 / N 天前」 */
export function fmtRelative(ms: number): string {
  if (!ms || Number.isNaN(ms)) return '从未使用';
  const diff = Date.now() - ms;
  if (diff < 0) return '刚刚';
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec} 秒前`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} 分钟前`;
  const hour = Math.floor(min / 60);
  if (hour < 24) return `${hour} 小时前`;
  const day = Math.floor(hour / 24);
  return `${day} 天前`;
}

export function generateRandomSuffix(length = 16): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

export interface DebouncedFunction<TArgs extends unknown[]> {
  (...args: TArgs): void;
  cancel: () => void;
}

/** 将连续调用合并为停止触发指定时间后的单次调用。 */
export function debounce<TArgs extends unknown[]>(
  callback: (...args: TArgs) => void,
  delay: number
): DebouncedFunction<TArgs> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const debounced = (...args: TArgs) => {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      timeoutId = undefined;
      callback(...args);
    }, delay);
  };

  debounced.cancel = () => {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
      timeoutId = undefined;
    }
  };

  return debounced;
}
